import React from 'react';
import { Card, Input, Button, Form, Select, DatePicker, Row, Col, Image, Avatar } from 'antd';
import AddressInput from '../../components/AddressInput';
import MediaInput from '../../components/ui/MediaInput';
import TrackSelector from '../../components/TrackSelector';

const { Option } = Select;

const EventFormSection = ({
  form,
  loading,
  handleCreateEvent,
  setShowEventForm,
  setHasScrapedAddress,
  setMediaUploadRef,
  setVideoStoryUploadRef,
  // Profile search props
  selectedProfile,
  searchResults,
  profilesLoading,
  searchLoading,
  handleProfileSearch,
  handleProfileSelect,
  // Location props
  countries,
  cities,
  handleCountryChange,
  handleAddressChange,
  hasScrapedAddress,
  // Music genres
  musicGenres,
  // AI description
  aiLoading,
  generateAIDescription,
  // RA Event ID for tracking
  raEventId
}) => {
  return (
    <Col xs={24} md={16}>
      <Card title="Create Event" className="mt-4">
        {/* Cover at the top of the form */}
        {form.getFieldValue('cover') && (
          <div className="mb-4" style={{ textAlign: 'center' }}>
            <Image
              src={form.getFieldValue('cover')}
              alt="Event cover"
              className="rounded-lg"
              style={{ maxHeight: '200px', objectFit: 'cover', marginBottom: 16 }}
            />
          </div>
        )}
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEvent}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="creatorProfileId"
                label="Creator Profile"
                rules={[{ required: true, message: 'Please select a creator profile!' }]}
              >
                <Select
                  placeholder="Search for a profile"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  onChange={handleProfileSelect}
                  value={selectedProfile?._id}
                  filterOption={false}
                  options={searchResults.map(profile => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={profile.picture} 
                          size={24} 
                          style={{ marginRight: 8 }}
                        />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[{ required: true }]}
              >
                <DatePicker 
                  showTime={{ format: 'HH:mm:ss' }}
                  format="YYYY-MM-DD HH:mm:ss" 
                  className="w-full" 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[{ required: true }]}
              >
                <DatePicker 
                  showTime={{ format: 'HH:mm:ss' }}
                  format="YYYY-MM-DD HH:mm:ss" 
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="musicTypes"
                label="Music Types"
                rules={[{ required: true, message: 'Please select at least one music type' }]}
              >
                <Select mode="multiple" placeholder="Select music types">
                  {musicGenres
                    .sort((a, b) => a.value.localeCompare(b.value))
                    .map(type => (
                      <Option key={type._id} value={type._id}>{type.value}</Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="eventType"
                label="Event Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="Club">Club</Option>
                  <Option value="Bar">Bar</Option>
                  <Option value="Festival">Festival</Option>
                  <Option value="Rave">Rave</Option>
                  <Option value="Speakeasy">Speakeasy</Option>
                  <Option value="Afterparty">Afterparty</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Description</span>
                    <Button
                      type="primary"
                      size="small"
                      icon={<span>🤖</span>}
                      loading={aiLoading}
                      onClick={generateAIDescription}
                      style={{ marginLeft: 8 }}
                    >
                      AI Generate
                    </Button>
                  </div>
                }
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="localisation"
                label="Address"
                rules={[{ required: true, message: 'Please enter an address' }]}
              >
                <AddressInput 
                  autoSelect={hasScrapedAddress} 
                  country={(() => {
                    const countryId = form.getFieldValue('country');
                    const selectedCountry = countries.find(c => c._id === countryId);
                    if (selectedCountry?.code === 'USA') return 'us';
                    if (selectedCountry?.code === 'CAN') return 'ca';
                    return null;
                  })()}
                  onChange={handleAddressChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: 'Please select a country' }]}
              >
                <Select
                  placeholder="Select country"
                  onChange={handleCountryChange}
                  showSearch
                  filterOption={(input, option) => {
                    const country = countries.find(c => c._id === option.value);
                    return country?.name?.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {countries.map(country => (
                    <Option key={country._id} value={country._id}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please select a city' }]}
              >
                <Select placeholder="Select city" showSearch>
                  {cities.map(city => (
                    <Option key={city._id} value={city._id}>
                      {city.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="organisationName"
                label="Organisation Name"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="publishTime"
                label="Publish Time"
                rules={[{ required: true }]}
              >
                <DatePicker showTime className="w-full" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="flyer"
                label="Flyer URL"
                rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
              >
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="externalLink"
                label="External Link"
                rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
              >
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="youtubeSet"
                label="YouTube Set URL"
                rules={[{ type: 'url', message: 'Please enter a valid YouTube URL' }]}
              >
                <Input placeholder="https://youtube.com/..." />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="embedmix"
                label="Embed Mix (Mixcloud)"
                rules={[{ type: 'url', message: 'Please enter a valid Mixcloud URL' }]}
              >
                <Input placeholder="https://www.mixcloud.com/..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="raEventId"
                label="RA Event ID"
              >
                <Input
                  value={raEventId || ''}
                  placeholder="Auto-extracted from RA URL"
                  style={{
                    backgroundColor: raEventId ? '#f6ffed' : '#fff2f0',
                    borderColor: raEventId ? '#b7eb8f' : '#ffa39e'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="affiliatedProfiles"
                label="Affiliated Profiles"
              >
                <Select
                  mode="multiple"
                  placeholder="Search and select affiliated profiles"
                  loading={profilesLoading || searchLoading}
                  showSearch
                  onSearch={handleProfileSearch}
                  filterOption={false}
                  options={searchResults.map(profile => ({
                    value: profile._id,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={profile.picture} 
                          size={24} 
                          style={{ marginRight: 8 }}
                        />
                        <span>{profile.username}</span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="tags"
                label="Tags"
              >
                <Select mode="tags" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="media"
                label="Media (Images/Videos)"
              >
                <MediaInput 
                  maxCount={6}
                  value={form.getFieldValue('media') || []}
                  onChange={(urls) => {
                    form.setFieldsValue({ media: urls });
                  }}
                  onFilesReady={(uploadRef) => {
                    setMediaUploadRef(uploadRef);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="videoStory"
                label="Video Story"
              >
                <MediaInput 
                  maxCount={1}
                  acceptedTypes={['video/*']}
                  value={form.getFieldValue('videoStory') ? [form.getFieldValue('videoStory')] : []}
                  onChange={(urls) => {
                    const videoUrl = urls.length > 0 ? urls[0] : '';
                    form.setFieldsValue({ videoStory: videoUrl });
                  }}
                  onFilesReady={(uploadRef) => {
                    setVideoStoryUploadRef(uploadRef);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="musicList"
                label="Music Tracks"
              >
                <TrackSelector 
                  maxCount={20}
                  value={form.getFieldValue('musicList') || []}
                  onChange={(trackIds) => {
                    form.setFieldsValue({ musicList: trackIds });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Event
            </Button>
            <Button
              className="ml-2"
              onClick={() => {
                setShowEventForm(false);
                setHasScrapedAddress(false);
                setMediaUploadRef(null);
                setVideoStoryUploadRef(null);
                form.resetFields();
                // Clear any selected profile and search results
                if (handleProfileSelect) {
                  handleProfileSelect(null);
                }
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Col>
  );
};

export default EventFormSection;